import { createFileRoute } from "@tanstack/react-router";
import { Calendar, MessageCircle, GraduationCap, CheckCircle2, Trophy } from "lucide-react";

import logoFeo from "@/assets/feo/logo-feo.png";

const URL = "https://fisioemortopedia.com.br/bolsas-feo-resultados";

export const Route = createFileRoute("/bolsas-feo-resultados")({
  head: () => ({
    meta: [
      { title: "Resultados · Programa Bolsas FEO 2026" },
      {
        name: "description",
        content:
          "Cronograma e resultados do Programa Bolsas FEO 2026, 10 bolsas integrais para fisioterapeutas. Divulgação em 14/07.",
      },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Resultados · Programa Bolsas FEO 2026" },
      {
        property: "og:description",
        content:
          "Cronograma e divulgação dos contemplados do Programa Bolsas FEO 2026, comunicação oficial pelo WhatsApp.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: ResultadosPage,
});

const CRONOGRAMA = [
  { data: "23/06", titulo: "Inscrições abertas", status: "feito" as const },
  { data: "02/07", titulo: "Inscrições encerradas", status: "feito" as const },
  { data: "05/07", titulo: "Prova online", status: "feito" as const },
  { data: "14/07", titulo: "Divulgação dos resultados", status: "atual" as const },
];

const CONTEMPLADOS_CPF = [
  "XXX.XXX.205-07",
  "XXX.XXX.578-84",
  "XXX.XXX.878-62",
  "XXX.XXX.534-31",
  "XXX.XXX.091-05",
  "XXX.XXX.179-21",
  "XXX.XXX.583-67",
  "XXX.XXX.996-17",
  "XXX.XXX.354-40",
  "XXX.XXX.717-92",
];

function ResultadosPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b0a1f] via-[#13123a] to-[#1a1850] text-white">
      <header className="border-b border-white/10 bg-black/20">
        <div className="mx-auto max-w-4xl px-3 sm:px-4 py-3 sm:py-4 flex items-center gap-3">
          <img decoding="async" src={logoFeo} alt="FEO" className="h-8 sm:h-9 w-auto" />
          <div className="h-7 w-px bg-white/15" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 truncate">
              Programa Bolsas FEO 2026
            </div>
            <div className="text-sm font-semibold truncate">Resultados oficiais</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-3 sm:px-4 py-8 sm:py-12 space-y-6">
        <div className="rounded-2xl border border-[#f5b022]/40 bg-[#f5b022]/5 backdrop-blur p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#f5b022]/15 border border-[#f5b022]/40 px-3 py-1 text-xs font-semibold text-[#f5b022]">
            <GraduationCap className="h-3.5 w-3.5" /> Programa Bolsas FEO 2026
          </div>
          <h1 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-bold leading-tight">
            Resultado oficial
          </h1>
          <p className="mt-3 text-sm sm:text-base text-white/75 leading-relaxed">
            Segue abaixo a relação dos <strong>10 contemplados</strong> com as bolsas integrais (100%)
            do Programa Bolsas FEO 2026. Por questões de privacidade (LGPD), publicamos apenas os
            <strong> últimos dígitos do CPF</strong>. O contato oficial com cada contemplado está sendo
            feito diretamente pelo <strong>nosso time no WhatsApp</strong>, com o passo a passo da
            matrícula.
          </p>
        </div>

        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 backdrop-blur p-5 sm:p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-300 flex items-center gap-2">
            <Trophy className="h-4 w-4" /> Contemplados · 10 bolsas integrais
          </h2>
          <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
            <div className="grid grid-cols-1">
              <div className="bg-white/10 px-4 py-2.5 text-center text-xs font-bold uppercase tracking-wider text-white/80">
                CPF
              </div>
              {CONTEMPLADOS_CPF.map((cpf) => (
                <div
                  key={cpf}
                  className="border-t border-white/10 bg-white/[0.03] px-4 py-3 text-center font-mono text-sm sm:text-base tabular-nums text-white"
                >
                  {cpf}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 sm:p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[#f5b022]" /> Cronograma oficial
          </h2>
          <ul className="mt-4 space-y-2">
            {CRONOGRAMA.map((c) => {
              const color =
                c.status === "feito"
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : "border-[#f5b022]/40 bg-[#f5b022]/10";
              return (
                <li key={c.data} className={`flex items-center gap-3 rounded-xl border p-3 ${color}`}>
                  <div className="font-mono text-sm font-bold tabular-nums w-14 shrink-0">{c.data}</div>
                  <div className="text-sm flex-1">{c.titulo}</div>
                  {c.status === "feito" ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#f5b022] shrink-0">
                      hoje
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 sm:p-6 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 mb-3 mx-auto">
            <MessageCircle className="h-6 w-6 text-emerald-400" />
          </div>
          <p className="text-sm text-white/80">
            Dúvidas sobre o resultado ou sobre a matrícula? Fale com o nosso time no WhatsApp:
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { label: "Contato 1 - Lucas", phone: "5511985788889" },
              { label: "Contato 2 - Joice", phone: "5511971810796" },
            ].map((c) => {
              const message = encodeURIComponent(
                "Olá! Estou na página de Resultados do Programa Bolsas FEO 2026 e preciso de ajuda.",
              );
              return (
                <a
                  key={c.phone}
                  href={`https://wa.me/${c.phone}?text=${message}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm font-semibold text-emerald-300 hover:bg-emerald-500/20 transition-colors"
                >
                  <MessageCircle className="h-4 w-4" />
                  {c.label}
                </a>
              );
            })}
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10 py-6 px-4 text-center text-xs text-white/40">
        Programa Bolsas FEO 2026 · Fisio em Ortopedia
      </footer>
    </div>
  );
}
